import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { LoaderSkeleton } from '@utilities';
import AttributeOptions from './components/AttributeOptions';

import styles from './_index.module.scss';

const AttributeOptionsResults = ({
  activeGroupedPrompt,
  activePrompt,
  activePromptAnswers,
  attributes: promptAttributes,
  isCustomLoading,
  isLoading,
  onAnswerChange,
  results,
  selectedAnswers,
}) => {
  const attributes = useMemo(() => {
    if (activeGroupedPrompt) {
      return (activeGroupedPrompt.attributes || []).map((attr) => ({
        ...attr,
        answerKey: activeGroupedPrompt.promptKey,
        maxValues: activeGroupedPrompt.maxValues,
        promptKey: activeGroupedPrompt.promptKey,
      }));
    }

    if (activePrompt?.groupedPrompts) {
      return activePrompt.groupedPrompts.reduce((acc, groupedPrompt) => {
        const groupedAttributes = (groupedPrompt.attributes || []).map((attr) => ({
          ...attr,
          answerKey: groupedPrompt.promptKey,
          maxValues: groupedPrompt.maxValues,
          promptKey: groupedPrompt.promptKey,
        }));
        return [...acc, ...groupedAttributes];
      }, []);
    }

    return (promptAttributes || activePrompt?.attributes || []).map((attr) => ({
      ...attr,
      answerKey: null,
      maxValues: activePrompt?.maxValues,
      promptKey: activePrompt?.promptKey,
    }));
  }, [activeGroupedPrompt, activePrompt, promptAttributes]);

  const userData = useSelector((state) => state?.user?.data);

  const buildAttributeOptionGroup = useCallback(
    (attribute) => {
      const promptKey = attribute?.promptKey;
      const data = results?.filter((result) => result?.attributeId === attribute?.id);

      return {
        answerKey: attribute?.answerKey,
        data,
        id: attribute?.id,
        maxValues: attribute?.maxValues,
        name: attribute?.name,
        promptAnswers: promptKey ? (selectedAnswers?.[promptKey] ?? activePromptAnswers ?? []) : [],
        promptKey,
      };
    },
    [activePromptAnswers, results, selectedAnswers]
  );

  const regularAttributes = useMemo(
    () =>
      attributes
        .filter((attribute) => !attribute?.isCustom)
        .map((attribute) => buildAttributeOptionGroup(attribute)),
    [attributes, buildAttributeOptionGroup]
  );

  const [customAttributes, fixedCustomAttributes] = useMemo(() => {
    const custom = [];
    const fixedCustom = [];
    attributes
      .filter((attribute) => attribute?.isCustom)
      .forEach((attribute) => {
        const optionGroup = buildAttributeOptionGroup(attribute);
        if (attribute?.isFixed) {
          fixedCustom.push(optionGroup);
        } else {
          custom.push(optionGroup);
        }
      });
    return [custom, fixedCustom];
  }, [attributes, buildAttributeOptionGroup]);

  const hasCustomResults = customAttributes.some((attr) => attr?.data?.length > 0);
  const hasFixedCustomResults = fixedCustomAttributes.some((attr) => attr?.data?.length > 0);
  const hasResults = regularAttributes.some((attr) => attr?.data?.length > 0);
  const hasCompatibleCustomGroupsOnly = activePrompt?.compatibleCustomGroupsLabel;

  const renderCustomGroupHeading = (groupType) => {
    if (groupType === 'Fixed') {
      return `${userData?.clientInfo?.name} Hierarchy${hasCompatibleCustomGroupsOnly ? ': Compatible Groups' : ''}`;
    }
    return `${hasCompatibleCustomGroupsOnly ? 'Compatible' : 'User'} Custom Groups`;
  };

  const renderCards = (list) => {
    return list.map((attribute, index) => {
      if (!attribute?.data?.length) return null;

      return (
        <div className={styles['attribute-options-results']} key={`attribute-custom-${index}`}>
          <span className={styles['attribute-options-results-left-column']}>{attribute?.name}</span>
          <div className={styles['attribute-options-results-right-column']}>
            <AttributeOptions
              max={attribute?.maxValues}
              onAnswerChange={onAnswerChange}
              options={attribute}
              promptAnswerKey={attribute?.answerKey}
              promptAnswers={attribute?.promptAnswers}
              promptKey={attribute?.promptKey}
            />
          </div>
        </div>
      );
    });
  };

  const renderLoader = () => {
    return (
      <LoaderSkeleton height={115}>
        <rect height="15" rx="2" ry="2" width="200" x="0" y="0" />
        <rect height="15" rx="2" ry="2" width="175" x="0" y="25" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="50" />
        <rect height="15" rx="2" ry="2" width="200" x="0" y="75" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="100" />
      </LoaderSkeleton>
    );
  };

  return (
    <div className={styles['attribute-options-results-wrapper']}>
      {isLoading && renderLoader()}
      {!isLoading &&
        hasResults &&
        regularAttributes?.map((attribute, index) => {
          if (!attribute?.data?.length) return null;

          return (
            <div className={styles['attribute-options-results']} key={`attribute-regular-${index}`}>
              <span className={styles['attribute-options-results-left-column']}>
                {attribute?.name}
              </span>
              <div className={styles['attribute-options-results-right-column']}>
                <AttributeOptions
                  max={attribute?.maxValues}
                  onAnswerChange={onAnswerChange}
                  options={attribute}
                  promptAnswerKey={attribute?.answerKey}
                  promptAnswers={attribute?.promptAnswers}
                  promptKey={attribute?.promptKey}
                />
              </div>
            </div>
          );
        })}
      {!isLoading && !hasResults && <div>No results found.</div>}
      {fixedCustomAttributes.length > 0 && (
        <>
          <h4 className={styles['attribute-options-sub-header']}>
            {renderCustomGroupHeading('Fixed')}
          </h4>
          {isCustomLoading && renderLoader()}
          {!isCustomLoading && hasFixedCustomResults && renderCards(fixedCustomAttributes)}
          {!isCustomLoading && !hasFixedCustomResults && <div>No custom results found.</div>}
        </>
      )}

      {customAttributes.length > 0 && (
        <>
          <h4 className={styles['attribute-options-sub-header']}>{renderCustomGroupHeading()}</h4>
          {isCustomLoading && renderLoader()}
          {!isCustomLoading && hasCustomResults && renderCards(customAttributes)}
          {!isCustomLoading && !hasCustomResults && <div>No custom results found.</div>}
        </>
      )}
    </div>
  );
};

AttributeOptionsResults.propTypes = {
  activeGroupedPrompt: PropTypes.object,
  activePrompt: PropTypes.object,
  activePromptAnswers: PropTypes.array,
  attributes: PropTypes.array,
  isCustomLoading: PropTypes.bool,
  isLoading: PropTypes.bool,
  onAnswerChange: PropTypes.func,
  results: PropTypes.array,
  selectedAnswers: PropTypes.object,
};

export default AttributeOptionsResults;
